import React, { useEffect, useState } from 'react';
import { getEventDateParts, toLocalDate, formatClockTime } from "../../helpers/eventDateTime";
import { useNavigate, useParams } from 'react-router-dom';
import API_UTIL from '../../services/AuthIntereptor';
import { toast } from 'react-toastify';
import AppBar from '../../components/AppBar/AppBar';
import { Icons, Button, Input, EventCover } from '../../components/ui';

const Collab = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userName, setUserName] = useState('');
  const [isUserNameExists, setIsUserNameExists] = useState(false);
  const navigate = useNavigate();
  const userPhoneNumber = localStorage?.userPhoneNumber;

  // Helper function to format event name
  const formatEventName = (eventName) => {
    return eventName ? eventName.replace(/_/g, ' ') : '';
  };

  // Helper function to format date
const formatEventDate = (event) => {
  // Date and time are independent and both optional. Anything unusable formats
  // as '' so the caller hides the row; a time on its own is still shown.
  const { date, time, endDate } = getEventDateParts(event);
  const clock = time ? formatClockTime(time) : '';
  if (!date) return clock;

  const start = toLocalDate(date);
  if (!endDate) {
    const day = start.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return clock ? `${day} at ${clock}` : day;
  }

  // Multi-day event — show the range, with the start time when there is one.
  const short = { weekday: 'short', month: 'short', day: 'numeric' };
  const range = `${start.toLocaleDateString('en-US', short)} - ${toLocalDate(endDate).toLocaleDateString('en-US', short)}`;
  return clock ? `${range} at ${clock}` : range;
};


  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const response = await API_UTIL.get(`/getEventDetails/${eventId}`);
        setEvent(response.data);
      } catch (error) {
        console.error('Error fetching event data:', error);
        toast.error('Failed to fetch event details.');
      }
    };

    const fetchUserDetails = async () => {
      try {
        const response = await API_UTIL.get(`/fetchUserDetails/${userPhoneNumber}`);
        setUserDetails(response.data.data);

        // Check if the user_name equals user_phone_number
        if (response.data.data.user_name !== userPhoneNumber) {
          setIsUserNameExists(true); // User name already exists
        }
        if (response.data.data.user_name === userPhoneNumber) {
          setUserName(''); // Set empty for phone numbers so user can enter new name
        } else {
          setUserName(response.data.data.user_name); // Keep existing username
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        toast.error('Failed to fetch user details.');
      }
    };

    fetchUserDetails();
    fetchEventData();
  }, [eventId, userPhoneNumber]);

  const updateCollabStatus = async (status) => {
    try {
        if (!isUserNameExists && (!userName || userName === userPhoneNumber)) {
          toast.error('Please enter a valid user name.');
          return;
        }

      // Update user details before accepting the collaboration
        let user_name;
        if (!isUserNameExists) {
          // User needs to set username, update it
          const response = await updateUserDetails(status);
          user_name = response.data.data.user_name;
        } else {
          // User already has a proper username, use it
          user_name = userDetails.user_name;
          // Update role to creator for existing users when accepting
          if (status === 'Accept') {
            await API_UTIL.post('/updatePortfolioDetails', {
              user_phone_number: userPhoneNumber,
              role: 'creator'
            });
          }
        }

      // Accept or reject the collaboration
      const apiResponse = await API_UTIL.post('/mapUserToEvent', {
        event_id: event.event_name,
        user_phone_number: userPhoneNumber
      });

      if (apiResponse.status === 200) {
        await API_UTIL.put(`/acceptCollab/${eventId}`, { userName: user_name, status });

        toast.success(`Collaboration ${status.toLowerCase()}ed successfully`);
        navigate('/event');
      }
    } catch (error) {
      console.error(`Error ${status.toLowerCase()}ing collaboration:`, error);
      toast.error(`Failed to ${status.toLowerCase()} collaboration. Please try again.`);
    }
  };

  const updateUserDetails = async (status) => {
    try {
      const org_name = userName; // Assume that userName is equivalent to org_name

      const updateData = {
        user_phone_number: userPhoneNumber,
        org_name: org_name,
        user_name: org_name,
        social_media: userDetails.social_media,
      };

      if (status === 'Accept') {
        updateData.role = 'creator';
      }

      const response = await API_UTIL.post('/updatePortfolioDetails', updateData);

      // toast.success('User details updated successfully');
      return response;
    } catch (error) {
      console.error('Error updating user details:', error);
      toast.error('Failed to update user details.');
    }
  };

  if (!event) {
    return (
      <>
        <AppBar />
        <div className="min-h-[60vh] grid place-items-center text-slate-500 text-sm">Loading...</div>
      </>
    );
  }

  return (
    <>
      <AppBar />
      <div className="fade-in max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Collaboration Invitation</h1>
        <p className="text-slate-500 mb-6">You have been invited to collaborate on this event.</p>

        {/* Event Details Card */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-card">
          <div className="relative aspect-[5/2]">
            <EventCover
              src={event.event_image || undefined}
              label={formatEventName(event.event_name)}
            />
          </div>
          <div className="p-5 sm:p-6">
            <h2 className="font-bold text-slate-900 text-lg leading-tight">
              {formatEventName(event.event_name)}
            </h2>
            <div className="mt-3 space-y-2 text-sm text-slate-600">
              {formatEventDate(event) && (
                <div className="flex items-center gap-2">
                  <Icons.Calendar className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>{formatEventDate(event)}</span>
                </div>
              )}
              {event.event_location && (
                <div className="flex items-start gap-2">
                  <Icons.Pin className="w-4 h-4 text-brand-600 mt-0.5 shrink-0" />
                  <span className="leading-snug">{event.event_location}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isUserNameExists && (
          <div className="mt-6">
            <Input
              label="User Name"
              id="userName"
              name="userName"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your user name"
              required
            />
          </div>
        )}

        <div className="mt-6 flex items-center gap-3">
          <Button onClick={() => updateCollabStatus('Accept')} icon={Icons.Check}>
            Accept
          </Button>
          <Button variant="secondary" onClick={() => updateCollabStatus('Reject')} icon={Icons.X}>
            Reject
          </Button>
        </div>
      </div>
    </>
  );
};

export default Collab;
